/**
 * Page guide content definitions for all FinTrack pages.
 *
 * Each export maps to a specific page and provides:
 *   - intro paragraph
 *   - key concepts / vocabulary
 *   - step-by-step walkthrough
 *   - pro tips
 *
 * [T66P0] Educational helper guides
 */
import type { PageGuideContent } from '@/components/common/PageGuide';

// ---------------------------------------------------------------------------
// Dashboard
// ---------------------------------------------------------------------------
export const dashboardGuide: PageGuideContent = {
  pageTitle: 'Dashboard',
  intro:
    'The Dashboard is your financial command center. It summarizes your net worth, monthly income vs. expenses, savings rate, recent transactions, budget progress, upcoming bills, and spending trends — all in one glance.',
  concepts: [
    { term: 'Net Worth', definition: 'Total assets minus liabilities across all accounts.' },
    { term: 'Savings Rate', definition: 'Percentage of income saved this month: (Income − Expenses) / Income × 100.' },
    { term: 'Cash Flow', definition: 'The difference between money coming in (income) and going out (expenses).' },
    { term: 'Spending by Category', definition: 'A breakdown showing which categories consume the most budget.' },
  ],
  steps: [
    { title: 'Review overview cards', description: 'Check Net Worth, Income, Expenses, and Savings Rate at the top.' },
    { title: 'Scan recent transactions', description: 'The "Recent Transactions" section shows the latest entries — click one to view details.' },
    { title: 'Track budget progress', description: 'Budget cards show how much you\'ve spent vs. your limits for the current month.' },
    { title: 'Monitor goals', description: 'Financial Goals cards display progress bars towards your savings targets.' },
  ],
  tips: [
    { text: 'All dashboard data updates automatically when you add or edit transactions.' },
    { text: 'Use the date-range selector (if visible) to view historical summaries.' },
    { text: 'Click any chart segment to drill down into the underlying transactions.' },
  ],
  options: [
    {
      name: 'Balanced',
      description: 'The default view. All sections visible — KPI cards, recent transactions, insights, budgets, and goals.',
      consequence: 'Full dashboard, nothing hidden. Use when you want a complete financial overview.',
      color: 'blue',
    },
    {
      name: 'KPIs',
      description: 'Highlights Net Worth, Income, Expenses, and Savings Rate front and center. Secondary sections are de-emphasized.',
      consequence: 'Fast morning check-in — your four headline numbers at a glance.',
      color: 'green',
    },
    {
      name: 'Planning',
      description: 'Elevates Budget progress and Goal cards. Charts and transaction feeds are de-emphasized.',
      consequence: 'Use on the last few days of the month when reviewing budget headroom or contributing to goals.',
      color: 'purple',
    },
  ],
};

// ---------------------------------------------------------------------------
// Accounts
// ---------------------------------------------------------------------------
export const accountsGuide: PageGuideContent = {
  pageTitle: 'Accounts',
  intro:
    'Accounts represent your real-world financial accounts — bank accounts, credit cards, investment portfolios, loans, and more. FinTrack tracks balances per account, and every transaction is linked to one.',
  concepts: [
    { term: 'Account Type', definition: 'The kind of account: Checking, Savings, Credit Card, Investment, Loan, Mortgage, Crypto, Manual, or Other.' },
    { term: 'Balance', definition: 'The current monetary value in the account. Positive = asset, Negative = liability.' },
    { term: 'Manual Account', definition: 'An account you maintain manually (no bank sync). Most accounts start as manual.' },
    { term: 'Cascade Delete', definition: 'Deleting an account also permanently removes all transactions linked to it.' },
  ],
  steps: [
    { title: 'Add an account', description: 'Click "Add Account" and fill in the name, type, balance, and currency. You can also set an emoji and color.' },
    { title: 'View details', description: 'Click any account row to open the Account Detail sheet — it shows balance, institution, interest rate, and timestamps.' },
    { title: 'Edit or delete', description: 'Use the ⋮ menu or the detail-sheet buttons. Deleting an account will also remove all associated transactions.' },
    { title: 'Track totals', description: 'The summary cards at the top show Total Balance, Assets, Liabilities, and Manual Account count.' },
  ],
  tips: [
    { emoji: '🏦', text: 'Create separate accounts for each real-world bank account to keep balances accurate.' },
    { emoji: '💱', text: 'Set the correct currency per account — transactions inherit the account\'s currency.' },
    { emoji: '⚠️', text: 'Before deleting an account, review its transactions first — they will be permanently removed.' },
  ],
};

// ---------------------------------------------------------------------------
// Transactions
// ---------------------------------------------------------------------------
export const transactionsGuide: PageGuideContent = {
  pageTitle: 'Transactions',
  intro:
    'Transactions are the core of FinTrack — every time money moves (earned, spent, or transferred between accounts), you record it here. Use filters, grouping, and column customization to view your data your way.',
  concepts: [
    { term: 'Income', definition: 'Money received — salary, freelance, interest, etc.' },
    { term: 'Expense', definition: 'Money spent — bills, groceries, entertainment, etc.' },
    { term: 'Transfer', definition: 'Moving money between two of your own accounts. Transfers don\'t count as income or expense.' },
    { term: 'Category', definition: 'A label like "Food", "Rent", or "Salary" used to classify transactions for budgets and reports.' },
    { term: 'Recurring', definition: 'A transaction that repeats on a schedule (e.g., monthly rent). Helps forecast future cash flow.' },
    { term: 'Required Fields', definition: 'Type, Amount, Description, Date, and Account are required for every transaction. Category is strongly recommended but optional.' },
  ],
  steps: [
    { title: 'Add a transaction', description: 'Click "Add Transaction". Required: Type (Income/Expense/Transfer), Amount (positive number), Description (searchable label), Date (when it occurred), Account (from which account). Optional: Category (for budgets/reports), Notes, Emoji, Recurring flag.' },
    { title: 'Filter and search', description: 'Click "Filter" to narrow by type, account, category, date range, or search text.' },
    { title: 'Group transactions', description: 'Use "Group By" to organize by Date, Type, Category, or Account for easier scanning.' },
    { title: 'Customize columns', description: 'Use the "Columns" button to show/hide table columns like Account, Notes, Tags, and Recurring status.' },
    { title: 'View details', description: 'Click any row to open the Transaction Detail sheet with full info, or use the eye icon.' },
    { title: 'Export data', description: 'Click "Export" to download your transactions as a CSV file.' },
  ],
  tips: [
    { text: 'Use Insights panel to see spending breakdown, top categories, and daily averages.' },
    { text: 'Categories are managed at the bottom of this page — scroll down to add or edit them.' },
    { text: 'For transfers, choose the source and destination accounts — balances auto-adjust.' },
    { text: 'Date format accepts dates via the calendar picker, or type in MM/DD/YYYY format.' },
    { text: 'Transfers require both a source and destination account. Select them carefully — balances on both accounts will be updated.' },
  ],
  options: [
    {
      name: 'Balanced',
      description: 'All columns visible. Insights panel toggle available. Standard filter bar.',
      consequence: 'Default view — nothing is hidden. Use for general browsing and data entry.',
      color: 'blue',
    },
    {
      name: 'Review',
      description: 'Columns curated for quick categorization: Description, Category, Amount, Date. Non-essential columns (Account, Notes, Tags, Recurring) auto-hidden.',
      consequence: 'Use when clearing a backlog of uncategorized or flagged transactions.',
      color: 'amber',
    },
    {
      name: 'Analysis',
      description: 'Insights panel auto-expanded. All data columns shown. Group-by default active.',
      consequence: 'Use for month-end analysis, spotting spending patterns, or preparing for a budget review.',
      color: 'green',
    },
  ],
};

// ---------------------------------------------------------------------------
// Review Inbox
// ---------------------------------------------------------------------------
export const reviewGuide: PageGuideContent = {
  pageTitle: 'Review Inbox',
  intro:
    'The Review Inbox surfaces transactions that need your attention — uncategorized entries, flagged items, and low-confidence auto-categories. Work through them with keyboard shortcuts or batch actions.',
  concepts: [
    { term: 'Uncategorized', definition: 'Transactions with no category assigned. Budget and report accuracy depends on keeping this bucket empty.' },
    { term: 'Flagged', definition: 'Transactions you or an automation rule marked for manual review.' },
    { term: 'Automation Suggestions', definition: 'FinTrack proposes categories based on transaction rules you\'ve saved. You can accept, edit, or dismiss each suggestion.' },
    { term: 'Preview-First', definition: 'When enabled in Settings, bulk rule commits show a diff before applying. Protects against accidental mass re-categorization.' },
  ],
  options: [
    {
      name: 'Balanced',
      description: 'Default view. Automation suggestions visible. Approve or skip each one individually. Full transaction detail shown.',
      consequence: 'Best for normal daily review — you stay in control with full context per item.',
      color: 'blue',
    },
    {
      name: 'Speed',
      description: 'Batch actions toolbar prominent. Automation suggestions hidden (override available). Transaction cards compact.',
      consequence: 'Use when clearing a large backlog. One-click "Mark Reviewed" without distraction. Suggestions still accessible via "Show Suggestions" button.',
      color: 'green',
    },
    {
      name: 'Audit',
      description: 'Full transaction detail always visible. Preview-first enforced for bulk actions. Automation suggestions shown with confidence score.',
      consequence: 'Use for end-of-month reconciliation. Every change is deliberate — no silent bulk operations.',
      color: 'red',
    },
  ],
  tips: [
    { text: 'Speed mode hides suggestions but always shows a "Show Suggestions" override button — you are never locked out.' },
    { text: 'Audit mode respects your Preview-First setting from Settings → Automation Preferences.' },
    { text: 'Session progress bar tracks how many items you\'ve cleared this session — refreshing resets the counter.' },
  ],
  warnings: [
    { text: 'Batch "Mark Reviewed" in Audit mode will preview affected items before committing.', severity: 'info' },
  ],
};

// ---------------------------------------------------------------------------
// Budgets
// ---------------------------------------------------------------------------
export const budgetsGuide: PageGuideContent = {
  pageTitle: 'Budgets',
  intro:
    'Budgets are named spending plans — you can create multiple per month (e.g., "Fixed Bills" and "Discretionary") to separate different spending pools. Set monthly limits per category and track actual spending against those limits.',
  concepts: [
    { term: 'Budget', definition: 'A **named** spending plan for a time period. You can create multiple budgets per month (e.g., "Monthly Bills", "Discretionary Spending", "Vacation Fund"). Each has its own category limits.' },
    { term: 'Category Budget', definition: 'A spending limit for one category within a budget (e.g., "Groceries: $200").' },
    { term: 'Budgeted', definition: 'The total amount you plan to spend across all categories.' },
    { term: 'Spent', definition: 'The actual amount spent so far (computed from matching expense transactions).' },
    { term: 'Remaining', definition: 'Budgeted minus Spent. Negative means you\'re over budget.' },
  ],
  steps: [
    { title: 'Create a budget', description: 'Click "Create Budget", give it a descriptive name, select the month and year. Optional: Method, Notes, Emoji, Color. Then add category spending limits.' },
    { title: 'Multiple budgets', description: 'Create separate budgets for different purposes — "Essentials", "Fun Money", "Savings Goals" — all in the same month.' },
    { title: 'Filter by period', description: 'Use the month/year dropdowns to view budgets for a specific period, or select "All" to see everything.' },
    { title: 'Track progress', description: 'Each budget card shows colored progress bars: green = under budget, yellow = close, red = over budget.' },
    { title: 'Edit categories', description: 'Open a budget to adjust per-category limits. Add new categories as your spending patterns evolve.' },
  ],
  tips: [
    { emoji: '📊', text: 'Create named budgets for different purposes — e.g., one for essential bills and another for fun money — rather than one giant budget.' },
    { emoji: '🎯', text: 'Start with generous limits and tighten them gradually as you see real spending patterns.' },
    { emoji: '🔔', text: 'Budget alerts appear in Notifications when spending approaches or exceeds a category limit.' },
  ],
};

// ---------------------------------------------------------------------------
// Goals
// ---------------------------------------------------------------------------
export const goalsGuide: PageGuideContent = {
  pageTitle: 'Goals',
  intro:
    'Financial Goals help you save towards specific targets — an emergency fund, vacation, debt payoff, or any custom target. Track progress, make contributions, and celebrate milestones.',
  concepts: [
    { term: 'Target Amount', definition: 'The total amount you want to save for this goal.' },
    { term: 'Current Amount', definition: 'How much you\'ve saved so far.' },
    { term: 'Contribution', definition: 'Adding money towards a goal. Each contribution is recorded with a date and optional note.' },
    { term: 'Status', definition: 'Active (in progress), Paused (temporarily stopped), or Completed (target reached).' },
    { term: 'On Track', definition: 'Whether your savings pace will reach the target by the deadline.' },
    { term: 'Target Date', definition: 'Optional. If set, FinTrack shows the required monthly contribution to meet your goal on time.' },
  ],
  steps: [
    { title: 'Create a goal', description: 'Click "Add Goal" and specify a name, target amount, target date, and category (savings, debt, custom).' },
    { title: 'Make contributions', description: 'Click "Contribute" on any goal card and enter the amount you\'re saving.' },
    { title: 'Contribute to a goal', description: 'Click the goal card and use "Add Contribution". Select the source account and the amount to transfer toward your goal.' },
    { title: 'Track progress', description: 'Progress bars show percentage completion. The "On Track" indicator shows if pace is sufficient.' },
    { title: 'Filter by status', description: 'Use the status filter to view only Active, Paused, or Completed goals.' },
  ],
  tips: [
    { emoji: '🚀', text: 'Break large goals into smaller milestones (e.g., "Save first $1,000 of $10,000").' },
    { emoji: '📅', text: 'Setting a target date turns your goal into a live tracker — you\'ll see exactly how much to save each month.' },
    { emoji: '🎉', text: 'Goal milestone notifications celebrate achievements as you hit 25%, 50%, 75%, and 100%.' },
  ],
};

// ---------------------------------------------------------------------------
// Analytics
// ---------------------------------------------------------------------------
export const analyticsGuide: PageGuideContent = {
  pageTitle: 'Analytics',
  intro:
    'The Analytics page gives you deep financial insights: how much you can safely spend, how long your savings last, net worth trends, anomaly detection, debt payoff strategies, and tax summaries.',
  concepts: [
    { term: 'Safe-to-Spend', definition: 'How much you can spend today without exceeding your budget or draining your emergency fund.' },
    { term: 'Financial Runway', definition: 'How many months your current savings can cover expenses at current spending rates.' },
    { term: 'Anomaly', definition: 'A transaction that\'s unusually large or rare compared to your normal spending pattern.' },
    { term: 'Debt Payoff Strategy', definition: 'Recommended repayment plans like Avalanche (highest interest first) or Snowball (smallest balance first).' },
  ],
  steps: [
    { title: 'Check Safe-to-Spend', description: 'See your remaining daily/weekly budget without jeopardizing savings goals.' },
    { title: 'Review runway', description: 'Financial Runway shows months of coverage if income stopped today.' },
    { title: 'Explore spending trends', description: 'The Spending Trends chart reveals category-level patterns over time.' },
    { title: 'Detect anomalies', description: 'Anomaly Detection flags unusual transactions for review.' },
  ],
  options: [
    {
      name: 'Balanced',
      description: 'All analytics sections shown at equal visual weight. Standard date range. Both Overview and Foresight tabs fully accessible.',
      consequence: 'Use for open-ended exploration — no sections are suppressed.',
      color: 'blue',
    },
    {
      name: 'Health Check',
      description: 'KPI cards, Financial Health Score radar, Safe-to-Spend, Runway, and Anomaly Detection are visually prominent. Deep charts (Budget Heatmap, Spending Velocity, Day-of-Week, Expense Calendar, Merchants) are de-emphasized (dimmed but still visible).',
      consequence: 'Use for a quick daily or weekly pulse check — surfaces actionable signals without data overload.',
      color: 'green',
    },
    {
      name: 'Deep Analysis',
      description: 'All sections at full prominence. A suggestion prompts switching to the 12-month date range for richer trend data.',
      consequence: 'Use for monthly or quarterly reviews. Every chart and metric is equally weighted for deep investigation.',
      color: 'purple',
    },
  ],
  tips: [
    { text: 'Analytics improve as you add more months of data — consistency is key.' },
    { text: 'Use the Calendar Forecast to plan for upcoming large expenses.' },
    { text: 'Tax Summary works best when transactions are properly categorized.' },
    { text: 'Health Check mode is ideal on mobile — it highlights your 5 most actionable numbers without requiring horizontal scrolling through deep charts.' },
    { text: 'Deep Analysis works best with at least 3 months of transaction data. The 12-month date range reveals seasonal patterns.' },
    { text: 'Switch between Foresight and Overview tabs regardless of which focus mode is active — presets only affect visual weight, never hide data.' },
  ],
};

// ---------------------------------------------------------------------------
// Reports
// ---------------------------------------------------------------------------
export const reportsGuide: PageGuideContent = {
  pageTitle: 'Reports',
  intro:
    'Reports aggregate your financial data into visual charts and summaries. View net worth over time, cash flow, savings rate trends, spending breakdowns, account balances, budget performance, and your overall Financial Health Score.',
  concepts: [
    { term: 'Financial Health Score', definition: 'A composite score from 0–100 measuring overall financial wellbeing based on savings, debt, budgets, and spending patterns.' },
    { term: 'Cash Flow', definition: 'Monthly chart showing income vs. expenses to see if you\'re gaining or losing money.' },
    { term: 'Budget Performance', definition: 'How well you stuck to each category budget — used vs. limit.' },
  ],
  steps: [
    { title: 'Browse report sections', description: 'Scroll through 10 report sections — each focuses on a different financial metric.' },
    { title: 'Read the health score', description: 'Your Financial Health Score at the bottom gives a holistic grade with a breakdown.' },
    { title: 'Compare time periods', description: 'Charts show trends over months — look for upward or downward changes.' },
  ],
  tips: [
    { text: 'For accurate reports, ensure all transactions are categorized and accounts are up-to-date.' },
    { text: 'The Recurring Expenses report helps identify subscriptions you might want to cancel.' },
  ],
};

// ---------------------------------------------------------------------------
// Subscriptions
// ---------------------------------------------------------------------------
export const subscriptionsGuide: PageGuideContent = {
  pageTitle: 'Subscriptions',
  intro:
    'Track your recurring subscriptions — Netflix, Spotify, gym memberships, cloud services, and more. See monthly costs, pause or cancel subscriptions, and get alerted when prices change.',
  concepts: [
    { term: 'Subscription', definition: 'A recurring payment for a service, billed on a regular schedule.' },
    { term: 'Billing Cycle', definition: 'How often you\'re charged: monthly, yearly, weekly, etc.' },
    { term: 'Price History', definition: 'A log of past prices, useful for detecting hidden price increases.' },
    { term: 'Status', definition: 'Active (being charged), Paused (temporarily stopped), Cancelled (no longer billed).' },
  ],
  steps: [
    { title: 'Add a subscription', description: 'Click "Add Subscription" and enter the name, cost, billing cycle, and start date.' },
    { title: 'View monthly total', description: 'The summary card shows your total monthly subscription cost.' },
    { title: 'Manage lifecycle', description: 'Use the ⋮ menu to Pause, Resume, or Cancel a subscription.' },
    { title: 'Review price history', description: 'Open a subscription to see if prices have changed over time.' },
  ],
  tips: [
    { emoji: '💰', text: 'Review subscriptions quarterly — many people forget about unused services.' },
    { emoji: '📊', text: 'The Reports page includes a Recurring Expenses section for subscription analysis.' },
  ],
};

// ---------------------------------------------------------------------------
// Bills
// ---------------------------------------------------------------------------
export const billsGuide: PageGuideContent = {
  pageTitle: 'Bills',
  intro:
    'Track upcoming bills, mark them as paid, and never miss a due date. Recurring bills auto-generate so you always know what\'s coming.',
  concepts: [
    { term: 'Bill', definition: 'A scheduled payment with a due date — rent, utilities, insurance, etc.' },
    { term: 'Recurring Bill', definition: 'A bill that repeats on a schedule. FinTrack auto-generates the next occurrence when you pay.' },
    { term: 'Pay', definition: 'Mark a bill as paid. This creates a linked transaction in your account.' },
    { term: 'Status', definition: 'Upcoming (not yet due), Paid (settled), Overdue (past due date), Cancelled (no longer active).' },
  ],
  steps: [
    { title: 'Add a bill', description: 'Click "Add Bill" and enter the name, amount, due date, and optional recurrence.' },
    { title: 'Mark as paid', description: 'Click the Pay action on any upcoming or overdue bill to record payment.' },
    { title: 'Filter by status', description: 'Use the tab filters to view All, Upcoming, Paid, or Overdue bills.' },
    { title: 'Review summary', description: 'The top cards show total upcoming, overdue, paid this month, and recurring totals.' },
  ],
  tips: [
    { emoji: '🔔', text: 'Set reminder days on each bill to get notified before the due date.' },
    { emoji: '🔄', text: 'Recurring bills auto-generate the next instance when you mark one as paid.' },
  ],
};

// ---------------------------------------------------------------------------
// Rules
// ---------------------------------------------------------------------------
export const rulesGuide: PageGuideContent = {
  pageTitle: 'Transaction Rules',
  intro:
    'Rules automatically categorize or tag transactions based on conditions you define. For example: "If description contains \'Starbucks\', set category to Food." Rules save time and ensure consistency.',
  concepts: [
    { term: 'Condition', definition: 'A test applied to a transaction field: "description contains X", "amount > Y", etc.' },
    { term: 'Action', definition: 'What happens when all conditions match: set category, add tag, change type, etc.' },
    { term: 'Priority', definition: 'Rules with higher priority execute first. If multiple rules match, the highest-priority one wins.' },
    { term: 'AND / OR Logic', definition: 'AND = all conditions must match. OR = any condition can match.' },
  ],
  steps: [
    { title: 'Create a rule', description: 'Click "Add Rule", define one or more conditions, and specify the action (e.g., set category, add tag).' },
    { title: 'Test a rule', description: 'Use "Test Rule" to see which existing transactions would match without actually changing them.' },
    { title: 'Apply all rules', description: 'Click "Apply All Rules" to run every rule against your entire transaction history.' },
    { title: 'Adjust priority', description: 'Drag or set number priority to control which rule takes precedence on conflicts.' },
  ],
  tips: [
    { text: 'Start with broad rules (e.g., "contains grocery") then refine with more specific ones.' },
    { text: 'Use regex conditions for complex matching patterns (e.g., "^AMAZON.*PRIME$").' },
    { text: 'Rules only auto-apply to future transactions by default — use "Apply All" for historical data.' },
  ],
};

// ---------------------------------------------------------------------------
// Notifications
// ---------------------------------------------------------------------------
export const notificationsGuide: PageGuideContent = {
  pageTitle: 'Notifications',
  intro:
    'Stay informed with real-time alerts about your finances. Notifications cover budget thresholds, bill reminders, goal milestones, spending anomalies, and subscription changes.',
  concepts: [
    { term: 'Priority', definition: 'Urgency level: critical (red), warning (yellow), info (blue). Critical items need immediate attention.' },
    { term: 'Unread', definition: 'Notifications you haven\'t reviewed yet — shown with a dot indicator and counted in the badge.' },
    { term: 'Notification Types', definition: 'Budget alert, Bill reminder, Goal milestone, Anomaly, Subscription change, System, and Custom.' },
  ],
  steps: [
    { title: 'Review alerts', description: 'New notifications appear with a highlighted background. Click to read the details.' },
    { title: 'Mark as read', description: 'Click the checkmark icon or view the notification to mark it as read.' },
    { title: 'Clear old notifications', description: 'Use "Clear All Read" to remove already-reviewed notifications.' },
    { title: 'Configure preferences', description: 'Go to Settings → Notifications to choose which alert types you want to receive.' },
  ],
  tips: [
    { text: 'Check notifications regularly — budget alerts can prevent overspending before it happens.' },
    { text: 'Goal milestone notifications celebrate progress and keep you motivated.' },
  ],
};

// ---------------------------------------------------------------------------
// Settings
// ---------------------------------------------------------------------------
export const settingsGuide: PageGuideContent = {
  pageTitle: 'Settings',
  intro:
    'Configure your FinTrack experience — update your profile, change the default currency, set themes, manage sessions, export/import data, and control notification preferences.',
  concepts: [
    { term: 'Default Currency', definition: 'The currency used for display and new accounts unless overridden (e.g., USD, EUR, IDR).' },
    { term: 'Theme', definition: 'Visual appearance: Light, Dark, or System (follows your OS preference).' },
    { term: 'Session', definition: 'An active login. View and revoke sessions from other devices for security.' },
    { term: 'Data Export', definition: 'Download all your financial data as JSON for backup or migration.' },
    { term: 'Data Import', definition: 'Upload a previously exported JSON file to restore or merge data.' },
  ],
  steps: [
    { title: 'Set your currency', description: 'Choose your default currency — it affects how amounts display across FinTrack, and defaults for new accounts.' },
    { title: 'Pick a theme', description: 'Select Light, Dark, or System theme to match your preference.' },
    { title: 'Update profile', description: 'Change your name, email, or password in the Profile section.' },
    { title: 'Export your data', description: 'In the Data Management section, click "Export" to download a full backup.' },
  ],
  tips: [
    { emoji: '🔒', text: 'Link your Google account for quick sign-in and an extra recovery option.' },
    { emoji: '💾', text: 'Export your data regularly as a personal backup strategy.' },
    { emoji: '🌍', text: 'Changing the default currency only affects display — existing account currencies are not changed.' },
  ],
};

// ---------------------------------------------------------------------------
// Insights (T113 — merged Reports + Analytics)
// ---------------------------------------------------------------------------
export const insightsGuide: PageGuideContent = {
  pageTitle: 'Insights',
  intro:
    'Insights is your unified financial intelligence hub — combining all reports and analytics into one powerful, tabbed interface. Explore KPIs, cash flow, spending patterns, budget performance, and wealth metrics in a single view.',
  concepts: [
    { term: 'Date Preset', definition: 'Quick-select time ranges (1m, 3m, 6m, 12m, YTD) that apply globally across all tabs.' },
    { term: 'Financial Health Score', definition: 'A composite score from 0–100 measuring overall financial wellness across 6 dimensions.' },
    { term: 'Spending Velocity', definition: 'Cumulative daily spending compared against previous months — helps predict month-end totals.' },
    { term: 'Budget Heatmap', definition: 'A matrix view showing budget adherence by category and month — green is under, red is over.' },
  ],
  steps: [
    { title: 'Select a date range', description: 'Use the date preset selector (top-right) to choose your analysis window.' },
    { title: 'Browse tabs', description: 'Switch between Overview, Cash Flow, Spending, Budget & Goals, and Wealth tabs.' },
    { title: 'Drill into charts', description: 'Each tab contains specialized charts — hover for tooltips and details.' },
    { title: 'Export', description: 'Use the Export button to download a summary report.' },
  ],
  tips: [
    { text: 'The Overview tab is a great daily check-in — KPIs and health score at a glance.' },
    { text: 'Spending tab reveals patterns: which days you spend most, top merchants, and category breakdown.' },
    { text: 'The Wealth tab tracks your long-term financial trajectory with net worth and debt payoff plans.' },
  ],
};

// ---------------------------------------------------------------------------
// Dashboards (T113 — custom dashboard builder)
// ---------------------------------------------------------------------------
export const dashboardsGuide: PageGuideContent = {
  pageTitle: 'Dashboards',
  intro:
    'Create custom dashboards tailored to your priorities. Drag widgets onto a grid, configure data sources and chart types, and build personalized views of your financial data.',
  concepts: [
    { term: 'Widget', definition: 'A configurable card showing a KPI, chart, or data table. Each widget pulls data from a specific source.' },
    { term: 'Data Source', definition: 'Where the widget gets its data: transactions, accounts, budgets, goals, bills, subscriptions, or analytics.' },
    { term: 'Grid Layout', definition: 'A 12-column grid where widgets snap into place. Resize widgets by adjusting their column span.' },
    { term: 'Template', definition: 'Pre-built dashboard configurations to get started quickly (Financial Overview, Budget Tracker, etc.).' },
  ],
  steps: [
    { title: 'Create a dashboard', description: 'Click "Create Dashboard" and choose a template or start blank.' },
    { title: 'Add widgets', description: 'Click widget types from the palette to add them to your grid.' },
    { title: 'Configure widgets', description: 'Click any widget to open configuration: set data source, metric, chart type, and title.' },
    { title: 'Save', description: 'Click Save to persist your dashboard layout and configurations.' },
  ],
  tips: [
    { text: 'Start with a template and customize from there — it\'s faster than building from scratch.' },
    { text: 'Use KPI widgets for quick metrics and chart widgets for trend analysis.' },
    { text: 'You can create multiple dashboards for different purposes (daily tracking, monthly review, etc.).' },
  ],
};
